/**
 * What the predictor looks at after they have locked in.
 *
 * DESIGN.md §3.5 put a quiet cream panel here reading "Both answers complete",
 * and that panel was the single deadest object in the game: two taps take about
 * eight seconds, the turn player takes a minute to drag twelve cards, and the
 * rest of that minute was spent looking at a sentence that had already been
 * true for fifty seconds. That is reversed. The panel is gone and the room is
 * here instead — who is still out, by face.
 *
 * ## What this may and may not show
 *
 * Faces and nothing else. `prediction_count.pending` carries player ids, never
 * picks: SPEC §2.3 keeps the *content* of every prediction private until phase
 * 4's attribution beat, and nothing on this board can leak it. Who has locked
 * in is not a prediction — it is the same fact the old counter carried, drawn
 * as people instead of as a number.
 *
 * The turn player never sees this board at all. The worker withholds
 * `prediction_count` from them entirely (they submit when they are ready — SPEC
 * §2.3 already puts the clock in their hands), so there is nothing to render.
 *
 * ## The collapsed variant
 *
 * The common case, by some distance, is that every predictor is in and the room
 * is waiting on one person. A grid of one is a strange object, so at that point
 * the board collapses to that person's card at full size — the same card the
 * photo screen uses — with the rest of the room reduced to a row of ticks
 * underneath. It reads as a room watching one person, which is what it is.
 */
import { LicenseCard, PlayerAvatar, type LicensePlayer } from '../LicenseCard.js';
import { predictionColumns } from './PredictionFaceGrid.js';

/**
 * What the room is actually waiting for, which is not the same thing in all
 * three states this board is rendered in.
 *
 * `ranking`  — phase 2. The turn player is still dragging cards.
 * `locking`  — phase 3, strip running. The ranking is already in; the only
 *              thing outstanding is stragglers locking (or the clock).
 * `locked`   — phase 3, past zero. Nothing is outstanding but the reveal.
 *
 * This used to be one string that named the turn player as "still ranking"
 * regardless, which was wrong in two of the three: the countdown exists
 * *because* the ranking was submitted, and at zero nobody is still predicting.
 * The board sat under a black strip reading ANSWERS LOCKED and contradicted it.
 */
export type PredictionWait = 'ranking' | 'locking' | 'locked';

export interface PredictionRoomBoardProps {
  /**
   * Connected predictors who have not locked in. Empty means the room is down
   * to the turn player, and the board collapses.
   */
  pending: readonly LicensePlayer[];
  /** The turn player, still dragging cards. */
  turnPlayer: LicensePlayer | null;
  /** Everyone who is in. Drawn small and ticked under the collapsed card. */
  locked?: readonly LicensePlayer[];
  /** What the room is waiting for. See `PredictionWait`. */
  wait?: PredictionWait;
  className?: string;
}

export function PredictionRoomBoard({
  pending,
  turnPlayer,
  locked = [],
  wait = 'ranking',
  className = '',
}: PredictionRoomBoardProps) {
  // Past zero the waiting list is spent — predictions are closed, so drawing
  // faces under "still predicting" would describe a race nobody is still in.
  const waiting = wait === 'locked' ? [] : pending;
  const collapsed = waiting.length === 0 && turnPlayer !== null;

  return (
    <div
      data-testid="prediction-room-board"
      data-collapsed={collapsed}
      className={`flex min-h-0 flex-1 flex-col justify-center gap-4 ${className}`}
    >
      {collapsed ? (
        <>
          <p className="text-center text-base font-semibold text-ink">
            {collapsedLine(wait, turnPlayer)}
          </p>
          {/* One card, at photo-screen size. Half the phone's width, centred:
              big enough to be a person rather than a progress indicator, small
              enough that the ticked room still fits under it. */}
          <div className="mx-auto w-1/2 max-w-[12rem]">
            <LicenseCard player={turnPlayer} variant="card" />
          </div>
          {locked.length > 0 && (
            <ul
              data-testid="prediction-locked-row"
              /* A centred `flex-wrap` broke eleven faces into a row of seven and
                 a row of four, centred against each other — a ragged shape that
                 reads as a layout accident rather than as the room. A grid with
                 a fixed column count wraps into even rows at every player count,
                 and `w-fit` keeps a short row centred without stretching it. */
              className="mx-auto grid w-fit grid-cols-6 items-start gap-x-2 gap-y-1.5"
            >
              {locked.map((player) => (
                <li key={player.id} className="relative" data-testid={`locked-${player.id}`}>
                  <PlayerAvatar player={player} size="h-10 w-10" shape="square" dimmed />
                  {/* The tick is the whole message, so it is the only thing on
                      the face that is not dimmed. Inset rather than hung off the
                      corner: at -1/-1 it sat half outside the face and collided
                      with the next one along. */}
                  <span
                    aria-hidden="true"
                    className="absolute right-0 bottom-0 flex h-4 w-4 items-center justify-center rounded-full bg-green text-[0.5625rem] font-semibold text-cream ring-2 ring-sand"
                  >
                    ✓
                  </span>
                  <span className="sr-only">{player.name} is in</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <p className="text-center text-[0.8125rem] text-ink-soft">
            {waitingLine(waiting.length, wait, turnPlayer)}
          </p>
          <ul
            data-testid="prediction-pending-grid"
            /*
              Fixed rows dividing the height it is given, exactly like the face
              grid it swaps in for — not auto rows.

              With auto rows this grid's height grew with the waiting list while
              its parent had no overflow handling and `.phone-column` is
              `overflow: hidden`, so the first seat to lock in a twelve-player
              room got ten faces in three rows, and the bottom row was clipped
              with no gesture that could reach it. The screen a predictor looks
              at for longest was the one that could lose part of itself.
            */
            className="mx-auto grid min-h-0 w-full flex-1 gap-2"
            style={{
              gridTemplateColumns: `repeat(${pendingColumns(waiting.length)}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${pendingRows(waiting.length)}, minmax(0, 1fr))`,
              // A short waiting list must not inflate to fill the phone: two
              // people outstanding should read as two cards, not two posters.
              // Capping both axes keeps the tile near the card proportion the
              // picker uses, and `justify-center` on the parent centres whatever
              // is left over instead of stretching it.
              maxWidth: `${pendingColumns(waiting.length) * 7}rem`,
              maxHeight: `${pendingRows(waiting.length) * 9.5}rem`,
            }}
          >
            {waiting.map((player) => (
              <li key={player.id} data-testid={`pending-${player.id}`}>
                <LicenseCard player={player} variant="card" size="tile" dimmed />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * The waiting list's own column count.
 *
 * Not `predictionColumns`: that function answers "how do twelve people fit on
 * one screen", and this grid holds a shrinking subset. Four pending faces in
 * three columns wraps 3 + 1; in two columns it is a square. So the columns
 * follow the list, capped at the face grid's own maximum so the board never
 * looks denser than the picker it replaced.
 */
function pendingColumns(count: number): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  return Math.min(predictionColumns(count), 4);
}

function pendingRows(count: number): number {
  return Math.max(1, Math.ceil(count / pendingColumns(count)));
}

/**
 * One line, naming what the room is waiting for. The turn player is named
 * because they are the reason the turn has not ended; the predictors are
 * counted rather than named because their faces are directly underneath.
 */
function waitingLine(
  count: number,
  wait: PredictionWait,
  turnPlayer: LicensePlayer | null,
): string {
  // Nobody left and nobody named: the turn is over bar the reveal. Reachable
  // only on a snapshot that arrives between the last lock and the phase change.
  if (count === 0) return 'Waiting for the reveal';
  const who = count === 1 ? '1 player' : `${count} players`;
  // Once the strip is running the ranking is already in, so the only thing
  // these faces are still doing is beating the clock.
  if (wait === 'locking') return `${who} still to lock in`;
  return turnPlayer
    ? `${who} still predicting · ${turnPlayer.name} is still ranking`
    : `${who} still predicting`;
}

/** The collapsed board's headline — the room is down to one thing. */
function collapsedLine(wait: PredictionWait, turnPlayer: LicensePlayer): string {
  // At zero, and during the countdown, the ranking is already submitted: the
  // countdown only exists because it was. Saying "still ranking" there was the
  // board contradicting the strip directly above it.
  if (wait === 'ranking') return `${turnPlayer.name} is still ranking`;
  return 'Waiting for the reveal';
}
